/**
 * Zod schemas for kanban status configuration on a hub.
 *
 * Single source of truth for cross-field validation invariants enforced on
 * every write path that lands `kanban_statuses` in HubDO SQLite:
 *   - REST: createHubBody / updateHubBody (workers/shared/src/contracts/setup/hubs.ts)
 *   - CI:   hubAsCodeConfigSchema         (workers/shared/src/contracts/ci.ts)
 *
 * Field shapes mirror the existing TypeScript interfaces:
 *   - HubAsCodeKanbanStatus / HubAsCodeKanbanFollowup (contracts/ci.ts)
 *   - KanbanStatusConfig    / FollowupConfig         (backend/scheduling/types.ts)
 *
 * Human-facing constraint summary lives in skills/wayai/SKILL.md "Kanban &
 * States — Constraints".
 *
 * `additional_context_schema` is a JSON Schema (Draft 2020-12). Structural
 * shape is gated here (size, reserved-name); semantic well-formedness
 * (`ajv.validateSchema`) runs at the backend route handler so AJV does not
 * leak into shared/frontend bundles.
 */

import { z } from 'zod';
import { withinJsonLength } from '../common';
import { SLUG_REGEX, slugSchema, visibleLength } from './slug';

// Defensive bounds. Typical hubs have 3–10 statuses with 0–3 followups each;
// caps are generous on intent and tight enough that the per-request
// superRefine cost stays bounded. Mirrors MAX_GRANTS in contracts/tokens.ts.
const MAX_KANBAN_STATUSES = 50;
const MAX_FOLLOWUPS_PER_STATUS = 20;
// Lanes group statuses, so #lanes ≤ #statuses in practice; this bound is generous.
const MAX_LANES = 20;
export const MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES = 16384;

/**
 * Reserved placeholder name in `additional_instructions` templates: dumps the
 * full payload as JSON. Schemas may not declare this as a top-level property.
 */
export const RESERVED_TEMPLATE_DUMP_NAME = 'additional_data';

/** Matches `{{path}}` and `{{ path.to.field }}` in templates. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;

/**
 * Kanban slug — alias of the shared SLUG_REGEX. Used as the immutable runtime
 * identifier (`conversation.kanban_status`, ClickHouse `data.meta.kanban_status`,
 * native tool params, transition refs).
 */
export const KANBAN_SLUG_REGEX = SLUG_REGEX;
export const kanbanStatusSlugSchema = slugSchema;

// =============================================================================
// FOLLOWUP SCHEMA
// =============================================================================

export const followupSchema = z
  .object({
    id: z.string().optional(),
    order: z.number().int().nonnegative(),
    type: z.enum(['inactivity', 'before_event']),
    threshold: z.number().int().positive(),
    timeUnit: z.enum(['seconds', 'minutes', 'hours', 'days']),
    instructions: z.string().optional(),
    excludedWeekDays: z.array(z.number().int().min(0).max(6)).optional(),
    excludeHolidays: z.boolean().optional(),
    excludedTimeStart: z.string().optional(),
    excludedTimeEnd: z.string().optional(),
    delivery_mode: z.enum(['agent_generated', 'direct']).optional(),
    direct_text: z.string().optional(),
    template_whatsapp_id: z.string().nullable().optional(),
    outside_window_policy: z.enum(['route_to_team', 'skip']).optional(),
    include_tools: z.boolean().optional(),
  })
  .passthrough()
  .superRefine((followup, ctx) => {
    if (followup.delivery_mode === 'direct' && !followup.direct_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['direct_text'],
        message: 'direct_text is required and must be non-empty when delivery_mode="direct"',
      });
    }
  });

// =============================================================================
// PER-STATUS SCHEMA
// =============================================================================

/**
 * Mutually-exclusive boolean flag pairs. Order within each pair is purely for
 * the error path: the issue is anchored on the second flag (the one the user
 * most recently toggled in typical UI flows).
 */
const EXCLUSIVE_FLAG_PAIRS = [
  ['isInitialStatus', 'triggersAgentResponse'],
  ['isInitialStatus', 'allowsAgentUpdate'],
  ['isInitialStatus', 'isTerminalStatus'],
  ['isInitialStatus', 'isSchedulingStatus'],
  ['triggersAgentResponse', 'allowsAgentUpdate'],
  ['triggersAgentResponse', 'isTerminalStatus'],
] as const;

export const kanbanStatusSchema = z
  .object({
    slug: kanbanStatusSlugSchema,
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be 100 characters or fewer')
      .refine((v) => visibleLength(v) > 0, 'name must not be blank'),
    order: z.number().int().nonnegative().optional(),
    color: z.string().optional(),
    isInitialStatus: z.boolean().optional(),
    triggersAgentResponse: z.boolean().optional(),
    allowsAgentUpdate: z.boolean().optional(),
    isTerminalStatus: z.boolean().optional(),
    isSchedulingStatus: z.boolean().optional(),
    eventName: z.string().optional(),
    additional_instructions: z.string().optional(),
    additional_context_schema: z
      .record(z.unknown())
      // `withinJsonLength`, not a bare `JSON.stringify(...).length`: the schema is
      // arbitrary caller JSON, and a value too deep to re-serialize would throw
      // RangeError here rather than being rejected (see its doc).
      .refine(
        (s) => withinJsonLength(s, MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES),
        `additional_context_schema must be at most ${MAX_ADDITIONAL_CONTEXT_SCHEMA_BYTES} bytes when JSON-serialized`,
      )
      .optional(),
    followups: z
      .array(followupSchema)
      .max(MAX_FOLLOWUPS_PER_STATUS, `followups must have at most ${MAX_FOLLOWUPS_PER_STATUS} entries`)
      .optional(),
    /** Slugs this status may transition to. `undefined` = unrestricted (any
     *  next status). Empty array `[]` is rejected — author must use
     *  `isTerminalStatus: true` to express "no outbound transitions". */
    allowed_next_statuses: z
      .array(z.string())
      .min(1, 'allowed_next_statuses cannot be empty — use isTerminalStatus: true for no outbound transitions')
      .optional(),
    /** Optional presentational grouping — the slug of a hub `lanes[]` entry this
     *  status belongs to. `undefined` = ungrouped. Purely a board-filter/organization
     *  concept; it does NOT constrain transitions (those live in `allowed_next_statuses`)
     *  and is not exposed to agents. The cross-reference invariant (must point at a
     *  declared lane; the initial status must not have one) is enforced at the hub
     *  level via `validateLaneReferences` — this schema only sees the status array. */
    lane_slug: kanbanStatusSlugSchema.optional(),
  })
  .passthrough()
  .superRefine((status, ctx) => {
    const label = status.name ? `"${status.name}"` : status.slug ? `"${status.slug}"` : '<unnamed>';

    for (const [a, b] of EXCLUSIVE_FLAG_PAIRS) {
      if (status[a] === true && status[b] === true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [b],
          message: `kanban status ${label}: ${a} and ${b} cannot both be true`,
        });
      }
    }

    if (status.isSchedulingStatus === true && !status.eventName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventName'],
        message: `kanban status ${label}: eventName is required when isSchedulingStatus=true`,
      });
    }

    if (
      Array.isArray(status.followups) &&
      status.followups.some((f) => f?.type === 'before_event') &&
      status.isSchedulingStatus !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['followups'],
        message: `kanban status ${label}: before_event followups require isSchedulingStatus=true on the parent status`,
      });
    }

    // Reserved-name collision: `additional_data` is the magic dump placeholder.
    if (status.additional_context_schema && typeof status.additional_context_schema === 'object') {
      const schema = status.additional_context_schema as Record<string, unknown>;
      const props = schema.properties;
      if (props && typeof props === 'object' && RESERVED_TEMPLATE_DUMP_NAME in (props as object)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['additional_context_schema', 'properties', RESERVED_TEMPLATE_DUMP_NAME],
          message: `kanban status ${label}: "${RESERVED_TEMPLATE_DUMP_NAME}" is a reserved template placeholder name and cannot be used as a top-level schema property`,
        });
      }
    }
  });

// =============================================================================
// ARRAY SCHEMA — cross-status invariants
// =============================================================================

export const kanbanStatusesArraySchema = z
  .array(kanbanStatusSchema)
  .max(MAX_KANBAN_STATUSES, `kanban_statuses must have at most ${MAX_KANBAN_STATUSES} entries`)
  .superRefine((statuses, ctx) => {
    if (statuses.length === 0) return;

    const initialIndices = statuses
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.isInitialStatus === true);

    if (initialIndices.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [],
        message: 'kanban_statuses: exactly one status must have isInitialStatus=true (found 0)',
      });
    } else if (initialIndices.length > 1) {
      const slugs = initialIndices.map(({ s }) => s.slug).join(', ');
      for (const { i } of initialIndices) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'isInitialStatus'],
          message: `kanban_statuses: only one status may have isInitialStatus=true (found ${initialIndices.length}: ${slugs})`,
        });
      }
    }

    // Unique slugs (slugs are the runtime identifier — duplicates corrupt routing).
    const seenSlugs = new Map<string, number[]>();
    statuses.forEach((s, i) => {
      const key = s.slug;
      if (!key) return;
      const arr = seenSlugs.get(key) ?? [];
      arr.push(i);
      seenSlugs.set(key, arr);
    });
    for (const [slug, indices] of seenSlugs) {
      if (indices.length > 1) {
        for (const i of indices) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'slug'],
            message: `kanban_statuses: duplicate slug "${slug}"`,
          });
        }
      }
    }

    // allowed_next_statuses entries must reference sibling slugs.
    const validSlugs = new Set(statuses.map((s) => s.slug).filter(Boolean));
    statuses.forEach((s, i) => {
      if (!Array.isArray(s.allowed_next_statuses)) return;
      s.allowed_next_statuses.forEach((target, j) => {
        if (typeof target !== 'string' || target.length === 0) return;
        if (!validSlugs.has(target)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'allowed_next_statuses', j],
            message: `kanban_statuses[${i}] (slug "${s.slug}"): allowed_next_statuses references unknown slug "${target}"`,
          });
        }
      });
    });
  });

// =============================================================================
// LANE SCHEMA — presentational grouping of kanban statuses
// =============================================================================

/**
 * A lane is an optional, hub-level grouping that statuses link to via `lane_slug`,
 * surfaced as a board filter. Presentational only — no transition semantics, not
 * agent-visible. Lives inside the hub config (alongside `kanban_statuses`), NOT as a
 * separate hub-as-code entity like eval journeys.
 */
export const laneSchema = z
  .object({
    slug: kanbanStatusSlugSchema,
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be 100 characters or fewer')
      .refine((v) => visibleLength(v) > 0, 'name must not be blank'),
    color: z.string().optional(),
    order: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export const lanesArraySchema = z
  .array(laneSchema)
  .max(MAX_LANES, `lanes must have at most ${MAX_LANES} entries`)
  .superRefine((lanes, ctx) => {
    // Unique slugs — slug is the reference target for `status.lane_slug`.
    const seen = new Map<string, number[]>();
    lanes.forEach((l, i) => {
      if (!l.slug) return;
      const arr = seen.get(l.slug) ?? [];
      arr.push(i);
      seen.set(l.slug, arr);
    });
    for (const [slug, indices] of seen) {
      if (indices.length > 1) {
        for (const i of indices) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [i, 'slug'], message: `lanes: duplicate slug "${slug}"` });
        }
      }
    }
  });

export interface LaneReferenceIssue {
  /** Path relative to the `kanban_statuses` array — callers prepend `'kanban_statuses'`. */
  path: (string | number)[];
  message: string;
}

/**
 * Cross-field invariant between the hub's `lanes` and its `kanban_statuses`. Lives
 * here (not in `kanbanStatusesArraySchema`) because that schema can't see `lanes`.
 * Called from the hub-level contract `superRefine` (request body) AND the backend
 * handler against the MERGED config — the latter is required because a partial PATCH
 * sends only one of the two arrays, so the request-body check alone can't catch a
 * `lane_slug` orphaned by a lanes-only edit (or vice versa).
 *
 *   2. A status's `lane_slug` (when set) must reference a declared lane.
 *   3. The initial status must NOT have a `lane_slug` (the shared-entry carve-out).
 */
export function validateLaneReferences(
  lanes: LaneInput[] | null | undefined,
  statuses: KanbanStatusInput[] | null | undefined,
): LaneReferenceIssue[] {
  const issues: LaneReferenceIssue[] = [];
  if (!Array.isArray(statuses) || statuses.length === 0) return issues;
  const declared = new Set((Array.isArray(lanes) ? lanes : []).map((l) => l.slug).filter(Boolean));

  statuses.forEach((s, i) => {
    const lane = s.lane_slug;
    if (typeof lane !== 'string' || lane.length === 0) return;
    const label = s.slug ? `"${s.slug}"` : `[${i}]`;
    if (s.isInitialStatus === true) {
      issues.push({
        path: [i, 'lane_slug'],
        message: `kanban status ${label}: the initial status cannot belong to a lane (it is the shared entry point)`,
      });
      return;
    }
    if (!declared.has(lane)) {
      issues.push({
        path: [i, 'lane_slug'],
        message: `kanban status ${label}: lane_slug references unknown lane "${lane}"`,
      });
    }
  });
  return issues;
}

// =============================================================================
// LINT — non-blocking warnings (returned alongside successful saves)
// =============================================================================

export interface KanbanLintWarning {
  code: 'unreachable' | 'placeholder_unresolved' | 'unused_lane';
  slug: string;
  name: string;
  message: string;
}

/**
 * Lint pass for kanban_statuses. Returns warnings that don't block save.
 *
 * - `unreachable`: a non-initial status that no other status lists in its
 *   `allowed_next_statuses`. Conversations cannot reach it.
 * - `placeholder_unresolved`: an `{{path}}` placeholder in `additional_instructions`
 *   that does not match any path in the declared `additional_context_schema`.
 *
 * - `unused_lane`: a declared lane that no status references via `lane_slug`.
 *   (Only emitted when `lanes` is passed.)
 *
 * Run after Zod validation succeeds; surface in the response so authors can
 * see warnings without being blocked mid-edit.
 */
export function lintKanbanStatuses(statuses: KanbanStatusInput[], lanes?: LaneInput[]): KanbanLintWarning[] {
  if (!Array.isArray(statuses) || statuses.length === 0) return [];

  const slugs = statuses.map((s) => s.slug).filter(Boolean);
  if (slugs.length === 0) return [];

  // -- Reachability ---------------------------------------------------------
  const allSlugs = new Set(slugs);
  const inboundFromAny = new Set<string>();
  const inboundExplicit = new Map<string, Set<string>>();

  for (const s of statuses) {
    if (!s.slug) continue;
    if (s.isTerminalStatus === true) continue;
    if (s.allowed_next_statuses === undefined) {
      for (const other of allSlugs) {
        if (other !== s.slug) inboundFromAny.add(other);
      }
    } else {
      for (const target of s.allowed_next_statuses) {
        if (!allSlugs.has(target)) continue;
        const set = inboundExplicit.get(target) ?? new Set<string>();
        set.add(s.slug);
        inboundExplicit.set(target, set);
      }
    }
  }

  const warnings: KanbanLintWarning[] = [];
  for (const s of statuses) {
    if (!s.slug) continue;
    if (s.isInitialStatus === true) continue;
    const hasInbound = inboundFromAny.has(s.slug) || (inboundExplicit.get(s.slug)?.size ?? 0) > 0;
    if (!hasInbound) {
      warnings.push({
        code: 'unreachable',
        slug: s.slug,
        name: s.name,
        message: `kanban status "${s.slug}" is unreachable — no other status transitions to it and it is not isInitialStatus`,
      });
    }
  }

  // -- Placeholder paths vs schema -----------------------------------------
  for (const s of statuses) {
    if (!s.slug || !s.additional_instructions) continue;
    const paths = extractPlaceholderPaths(s.additional_instructions);
    if (paths.length === 0) continue;
    const schema = s.additional_context_schema;
    for (const path of paths) {
      if (path === RESERVED_TEMPLATE_DUMP_NAME) continue;
      if (!schema || !isPathInJsonSchema(schema, path)) {
        warnings.push({
          code: 'placeholder_unresolved',
          slug: s.slug,
          name: s.name,
          message: `kanban status "${s.slug}": template placeholder "{{${path}}}" does not match any path in additional_context_schema`,
        });
      }
    }
  }

  // -- Unused lanes (declared but unreferenced) ----------------------------
  if (Array.isArray(lanes) && lanes.length > 0) {
    const referenced = new Set(statuses.map((s) => s.lane_slug).filter((v): v is string => typeof v === 'string' && v.length > 0));
    for (const lane of lanes) {
      if (!lane.slug || referenced.has(lane.slug)) continue;
      warnings.push({
        code: 'unused_lane',
        slug: lane.slug,
        name: lane.name,
        message: `lane "${lane.slug}" is declared but no kanban status is assigned to it`,
      });
    }
  }

  return warnings;
}

function extractPlaceholderPaths(template: string): string[] {
  const out = new Set<string>();
  for (const match of template.matchAll(PLACEHOLDER_RE)) {
    out.add(match[1]);
  }
  return Array.from(out);
}

/**
 * Lint-side path resolver against a JSON Schema (Draft 2020-12 subset).
 * Walks `properties` for objects and `items` for arrays. Numeric segments
 * after an array indirect into `items`. Refs / oneOf / unknown shapes
 * return false (lint is best-effort; runtime renders empty string anyway).
 *
 * Treats an absent `type` as object-like when `properties` is present
 * (legal per JSON Schema spec — type is not required) and array-like when
 * `items` is present. Without these, schemas authored as raw YAML by CLI/MCP
 * users (who often omit `type`) would false-positive every placeholder.
 */
function isPathInJsonSchema(schema: unknown, path: string): boolean {
  if (typeof schema !== 'object' || schema === null) return false;
  const segments = path.split('.');
  let cur: unknown = schema;
  for (const seg of segments) {
    if (typeof cur !== 'object' || cur === null) return false;
    const obj = cur as Record<string, unknown>;
    const propsObj = obj.properties && typeof obj.properties === 'object' ? (obj.properties as Record<string, unknown>) : null;
    const itemsObj = obj.items && typeof obj.items === 'object' ? (obj.items as Record<string, unknown>) : null;
    const isObjectLike = obj.type === 'object' || (obj.type === undefined && propsObj !== null);
    const isArrayLike = obj.type === 'array' || (obj.type === undefined && itemsObj !== null && propsObj === null);
    if (isObjectLike && propsObj) {
      if (!(seg in propsObj)) return false;
      cur = propsObj[seg];
    } else if (isArrayLike && itemsObj) {
      if (!/^\d+$/.test(seg)) return false;
      cur = itemsObj;
    } else {
      return false;
    }
  }
  return true;
}

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type FollowupInput = z.infer<typeof followupSchema>;
export type KanbanStatusInput = z.infer<typeof kanbanStatusSchema>;
export type KanbanStatusesInput = z.infer<typeof kanbanStatusesArraySchema>;
export type LaneInput = z.infer<typeof laneSchema>;
export type LanesInput = z.infer<typeof lanesArraySchema>;
